import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import GlobalSettings from './GlobalSettings';
import Branding from './Branding';
import HallucinationsSection from './HallucinationsSection';
import { Globe, Radio, ImagePlay, EyeOff } from 'lucide-react';
import { SettingsSubnav, SettingsSubnavTab } from './SettingsSubnav';
import {
  SettingsPageHero,
  SettingsSectionWidth,
  settingsMainCardClass,
} from './SettingsSectionLayout';

const CONFIG_TABS = [
  { id: 'display-language', label: 'Display & Language', shortLabel: 'Display', Icon: Globe },
  { id: 'transcription-services', label: 'Transcription Services', shortLabel: 'Transcription', Icon: Radio },
  { id: 'branding', label: 'Branding', shortLabel: 'Branding', Icon: ImagePlay },
  { id: 'audio-post-processing', label: 'Audio Post processing', shortLabel: 'Audio', Icon: EyeOff },
];

const GlobalSettingsSection = ({
  globalSettings,
  handleGlobalChange,
  isDarkMode,
  timezone,
  timeFormat,
  setTimeFormat,
  reverseSort,
  setReverseSort,
  user,
  edgeServerEndpoint,
  showToast,
  handleBackupNow,
  keywords,
  newKeyword,
  setNewKeyword,
  handleAddKeyword,
  handleRemoveKeyword
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('display-language');

  useEffect(() => {
    const globalTab = searchParams.get('globalTab');
    if (globalTab && ['display-language', 'transcription-services', 'branding', 'audio-post-processing'].includes(globalTab)) {
      setActiveTab(globalTab);
    } else {
      setActiveTab('display-language');
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: 'global', globalTab: tabId });
  };

  const renderSection = () => (
    <GlobalSettings
      globalSettings={globalSettings}
      handleGlobalChange={handleGlobalChange}
      isDarkMode={isDarkMode}
      timezone={timezone}
      timeFormat={timeFormat}
      setTimeFormat={setTimeFormat}
      reverseSort={reverseSort}
      setReverseSort={setReverseSort}
      user={user}
      edgeServerEndpoint={edgeServerEndpoint}
      activeSection={activeTab}
    />
  );

  const card = settingsMainCardClass(isDarkMode);

  return (
    <SettingsSectionWidth>
      <SettingsPageHero
        isDarkMode={isDarkMode}
        title="Configurations"
        description="Configure system-wide defaults for language, transcription, branding, and audio processing."
        icon={<span className="material-symbols-outlined text-2xl">settings_input_component</span>}
      />

      <div className={`rounded-xl border p-6 md:p-8 ${card}`}>
        <SettingsSubnav isDarkMode={isDarkMode} embedded aria-label="Configuration sections">
          {CONFIG_TABS.map(({ id, label, shortLabel, Icon }) => (
            <SettingsSubnavTab
              key={id}
              isDarkMode={isDarkMode}
              active={activeTab === id}
              onClick={() => handleTabChange(id)}
              className="flex max-w-max shrink-0 items-center gap-1 md:gap-2"
            >
              <Icon className="h-4 w-4 shrink-0 md:h-5 md:w-5" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{shortLabel}</span>
            </SettingsSubnavTab>
          ))}
        </SettingsSubnav>

        {activeTab === 'branding' && (
          <Branding
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab === 'audio-post-processing' && (
          <HallucinationsSection
            keywords={keywords}
            newKeyword={newKeyword}
            setNewKeyword={setNewKeyword}
            handleAddKeyword={handleAddKeyword}
            handleRemoveKeyword={handleRemoveKeyword}
            globalSettings={globalSettings}
            handleGlobalChange={handleGlobalChange}
            isDarkMode={isDarkMode}
            edgeServerEndpoint={edgeServerEndpoint}
          />
        )}
        {activeTab !== 'branding' && activeTab !== 'audio-post-processing' && (
          renderSection()
        )}
      </div>
    </SettingsSectionWidth>
  );
};

export default GlobalSettingsSection;
